/**
 * 🔺 `headerActions` — A CONTROL ON A SECTION HEADER (2026-08-23).
 *
 * MOVED HERE FROM `client/src/components/ui/CLAUDE.md` on 2026-09-05, and the
 * move IS the point: this component lives in `client/src/components/`, which has
 * NO co-located `CLAUDE.md`, so the harness injected these rules for whoever was
 * editing something under `ui/` and NEVER for the person editing the component
 * they govern. `ui/CLAUDE.md` keeps a one-line pointer at this file. (Root
 * `CLAUDE.md`: the RULE belongs in a topic file, the ARCHAEOLOGY belongs in the
 * docblock AT THE SITE — unbudgeted, beside the code.)
 *
 * A section's primary create belongs ON THE HEADER ROW, not on a row of its own
 * at the top of the body. Measured on the budget page in Chromium at 1440x900:
 * moving `+ Add Cost Code` into `headerActions` took the section body from 873px
 * to 846px and the document from 1205px to 1178px — 27px per section, and the
 * header row does not grow to take it (44px before and after). The owner's
 * standing complaint is vertical space; this is the section-level version of the
 * two-band chrome's job.
 *
 * 🚨 THE HEADER IS A `role="button"` WITH `tabIndex={0}`, SO ANYTHING YOU PUT IN
 * IT SHARES ITS KEYBOARD. The `headerActions` wrapper stops `click` and
 * `mousedown`, and until 2026-08-23 it stopped NOTHING on the keyboard: a SPACE
 * typed into a header input matched the header's own `' '` case, ran
 * `preventDefault()` and collapsed the section, and the space never reached the
 * input either, so a multi-word value could not be typed at all. 🔑 The fix is a
 * TARGET CHECK in the header's own handler, never a third `stopPropagation` on
 * the wrapper — the full reasoning is at the `onKeyDown` comment below, where
 * the check lives. Pinned by `CollapsibleSection.headerActionsKeys.test.js`.
 *
 * 🪤 THE BUG ONLY BITES A SECTION THAT IS NOT `forceExpanded`, AND THAT NEARLY
 * MADE THE TEST VACUOUS. The old handler read `!forceExpanded && (…)` with the
 * `!forceExpanded` term FIRST, so on a force-expanded section it never reached
 * `preventDefault()` at all. The budget page force-expands whichever tab is
 * active, so on DESKTOP the defect is unreachable and on MOBILE — a stacked
 * accordion where only one section is force-expanded — every other section has
 * it. The first version of `BudgetCostCodes.sectionHeaderActions.test.js` §3
 * pointed at the default active tab and PASSED WITH THE GUARD DELETED; it now
 * targets a non-active section and asserts `role="button"` on the header first,
 * so the premise cannot silently expire.
 *
 * 🎨 A filled `BTN_XS.primary` is correct here and does not breach the
 * one-filled-control rule in `ui/CLAUDE.md`. That rule governs what rests on a
 * PAGE's chrome; a section header is not page chrome, and on desktop only one
 * section is open at a time, so exactly one filled create is ever on screen —
 * which is the outcome the rule protects. ⚠️ NOT EVERY SECTION QUALIFIES. The
 * budget page moved four lone `+ Add …` toggles and deliberately left Labour
 * Flow's `+ Add Member` alone: it shares a toolbar with the view switcher and
 * the Save/Cancel pair, and pulling one control out of a group is a different
 * change from moving a button that stands by itself.
 *
 * 🔻 `hideHeaderAtMd` (2026-10-05, owner: *"yes build it into the preview for
 * desktop please"*) — Labour Flow, and since 2026-10-29 the Cost Codes & Budget
 * tab, whose two NESTED sections (Cost codes, Job codes) carry the headers on
 * desktop (owner: *"two equaly important sections which can collpase and
 * expand"*). On desktop the section tab above
 * already names the section, so the title row is redundant height; its
 * controls moved to a sticky `ui/Toolbar` inside the body. It HIDES the row at
 * `md`+ with CSS (the row stays mounted, so `forceExpanded` and the `id`
 * plumbing are untouched) and drops the content's top hairline there, which
 * would otherwise cut between the active tab and its panel. Default `false`:
 * every other section — Cost Codes' `+ Add Cost Code` header included — is
 * byte-identical. Below `md` nothing changes: the accordion still needs its
 * header.
 *
 * 📱 `phoneAccordion` (2026-10-11, owner: *"on phone they should all start
 * collapsed on every page load/refresh"*) — Admin Settings ONLY. Below `md`
 * the page is a stack of all nine sections with no tab strip, and the active
 * tab was `forceExpanded` there too: one section could never be folded and
 * the rest opened by default, so reaching Users meant scrolling past
 * Productions, Departments and Suppliers. With the prop, on a phone the
 * section IGNORES `forceExpanded`, starts folded on every load, and neither
 * reads nor writes the saved state (a stored "open" must not undo "start
 * collapsed"). At `md`+ nothing changes. Pinned by
 * `CollapsibleSection.phoneAccordion.test.js`.
 *
 * ➕ `subtitle` + `expandWhen` (2026-10-29, the Cost Codes & Budget tab's two
 * nested sections). `subtitle`: a muted line under the title, visible folded.
 * `expandWhen`: a header `+ Add …` whose form lives in the BODY must open a
 * folded section, or the click reveals nothing — pass the form's open flag.
 * Both default off, and with neither passed a section's DOM is unchanged —
 * with ONE deliberate exception for EVERY caller, named here because review
 * 1029B (A-4) caught the earlier "every other section's DOM unchanged" claim:
 * the chevron `<svg>` gained `shrink-0`. Corrective, not cosmetic — measured in
 * a Chromium replica of this header, when the title row's min-content exceeds
 * the header (a long title, or title + actions in a narrow card) the old
 * chevron was squeezed to 0px wide and VANISHED; with `shrink-0` it stays 20px.
 * Where the row fits (every caller measured so far) the two render the same. A NESTED
 * section is safe inside another's body: its header is not inside the outer
 * header, so neither its clicks nor its keys reach the outer toggle. Pinned by
 * `CollapsibleSection.subtitleExpandWhen.test.js`.
 */
import React, { useEffect, useState } from 'react';
import { isMobileOrderViewport, useIsMobileOrderViewport } from '../hooks/useIsMobileOrderViewport';

const LS_KEY_PREFIX = 'sfx_collapsed_';

function CollapsibleSection({ id, title, children, className = '', defaultCollapsed = false, headerActions = null, onToggle = null, forceExpanded: forceExpandedProp = false, hideHeaderAtMd = false, phoneAccordion = false, subtitle = null, expandWhen = false }) {
  const phoneFold = useIsMobileOrderViewport() && phoneAccordion;
  const forceExpanded = forceExpandedProp && !phoneFold;
  const [collapsed, setCollapsed] = useState(() => {
    if (phoneAccordion && isMobileOrderViewport()) return true;
    try {
      const saved = localStorage.getItem(LS_KEY_PREFIX + id);
      if (saved !== null) return saved === 'true';
      return defaultCollapsed;
    } catch {
      return defaultCollapsed;
    }
  });

  // forceExpanded overrides internal state — used when an outer container
  // (e.g. page-level tabs) is responsible for showing/hiding the whole section.
  // `expandWhen` (2026-10-29): while true the section renders OPEN in the same
  // commit (derived, so a form it reveals is laid out before the caller's own
  // effect scrolls to it), and the open state is then stored as if the user had
  // opened it. A header click while it holds still reaches `onToggle(true)`,
  // whose caller clears the flag — so the section folds on the next render.
  const effectiveCollapsed = forceExpanded ? false : (collapsed && !expandWhen);
  useEffect(() => {
    if (!expandWhen || !collapsed || forceExpanded) return;
    setCollapsed(false);
    if (!phoneFold) {
      try {
        localStorage.setItem(LS_KEY_PREFIX + id, 'false');
      } catch {
        // ignore
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandWhen]);

  const toggle = () => {
    if (forceExpanded) return;
    const next = !collapsed;
    setCollapsed(next);
    if (!phoneFold) {
      try {
        localStorage.setItem(LS_KEY_PREFIX + id, String(next));
      } catch {
        // ignore
      }
    }
    if (onToggle) onToggle(next);
  };

  return (
    <div className={`bg-white rounded-lg shadow-md ${className}`} data-testid={`collapsible-${id}`} data-collapsed={effectiveCollapsed ? 'true' : 'false'}>
      <div
        onClick={toggle}
        className={`group w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${forceExpanded ? 'dark:bg-slate-700/30' : 'hover:bg-gray-50 dark:bg-slate-700/30 dark:hover:bg-slate-700/50 cursor-pointer'}${hideHeaderAtMd ? ' md:hidden' : ''}`}
        role={forceExpanded ? undefined : 'button'}
        tabIndex={forceExpanded ? undefined : 0}
        aria-expanded={!effectiveCollapsed}
        data-testid={`collapsible-toggle-${id}`}
        /* 🚨 `e.target === e.currentTarget` — THE HEADER TOGGLES ONLY FOR A KEY
           AIMED AT THE HEADER ITSELF, never for one bubbling out of something
           inside it.

           This row is a `role="button"` + `tabIndex={0}` and `headerActions`
           renders INSIDE it, so before the check every keydown from a header
           control reached here. A SPACE typed into a header input matched
           `' '`, ran `preventDefault()` and collapsed the section — the space
           never reached the input either, so a multi-word value could not be
           typed at all.

           🔑 IT IS A TARGET CHECK RATHER THAN A THIRD `stopPropagation` ON THE
           `headerActions` WRAPPER BELOW (which already stops click and
           mousedown). The wrapper form fixes one child; this form fixes the
           title, the chevron and every control anybody adds here later. A rule
           that has to be re-applied per child is a rule that gets forgotten,
           and nothing would go red when it is.

           🪤 A native `<button>` in the header is unaffected: the browser
           synthesises its own click for Enter/Space, which is what the control
           responds to. Nothing here re-dispatches anything.

           Pinned by `CollapsibleSection.headerActionsKeys.test.js`. */
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (!forceExpanded && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(); }
        }}
      >
        {/* 📱 `max-md:flex-wrap` (mobile batch item 20): with `headerActions`
            beside it, a long title was squeezed to three lines ("Cost Codes &
            Budget" at 375px). Wrapping lets the actions take the next line
            when — and only when — the two do not fit; md+ is untouched. */}
        {(() => {
          const titleRow = (
            <div className="flex items-center gap-2 max-md:flex-wrap max-md:gap-y-1">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">{title}</h3>
              {headerActions && <div className="relative" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>{headerActions}</div>}
            </div>
          );
          // `subtitle` (2026-10-29): one muted line under the title row, so a
          // section can say what it holds while folded. Absent → the row above
          // renders exactly as before, with no wrapper.
          if (subtitle == null) return titleRow;
          return (
            <div className="min-w-0">
              {titleRow}
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400" data-testid={`collapsible-subtitle-${id}`}>{subtitle}</p>
            </div>
          );
        })()}
        {!forceExpanded && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 shrink-0 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all duration-200 ${effectiveCollapsed ? '' : 'rotate-180'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </div>
      <div
        className={`border-t border-gray-100 dark:border-gray-700${hideHeaderAtMd ? ' md:border-t-0' : ''}`}
        data-testid={`collapsible-content-${id}`}
        style={effectiveCollapsed ? { display: 'none' } : undefined}
      >
        {children}
      </div>
    </div>
  );
}

export default CollapsibleSection;
